import { lazy, Suspense, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { graphyte } from '@comeon/analytics';
import Cookies from '@comeon/tomcat-cookies';
import { Spinner } from '@comeon/ui';

import CRMComponents from '~/components/account/crm-container';
import { accountUpdate, fetchCurrency } from '~/components/account/ducks';
import { AppBridge, isBridgeAvailable } from '~/components/app-bridge';
import { ReCaptcha } from '~/components/authentication/async';
import { updateSession, validateSession } from '~/components/authentication/ducks';
import { selectAuthenticated } from '~/components/authentication/ducks/selectors';
import { useDesktopStateManager } from '~/components/brand/desktop-state-manager';
import { selectGuestLocale, selectHasProducts, selectIsGuestPlayer } from '~/components/brand/ducks/selectors';
import { setPreviousLocation } from '~/components/casino/ducks';
import CookieBannerContainer from '~/components/cookie-banner/index';
import RootErrorBoundary from '~/components/root-error-boundary';
import GuestFlowAutostart from '~/components/misc/guest-flow-autostart';
import HelmetTagWrapper from '~/components/misc/helmet-tag-wrapper';
import IframeSpinner from '~/components/misc/iframe-spinner';
import NullLoader from '~/components/null-loader';
import RegulationsContainer from '~/components/regulations-container';
import { dataLayerChanges, queryChanges } from '~/components/router/on-route-change';
import Template from '~/components/template';
import InitialUIContext from '~/components/template/initial-ui-context';
import { ThemeProvider } from '~/components/theme/theme-context';
import WeSpinAppComponents from '~/components/we-spin/old-flow/we-spin-app-components';
import usePrevious from '~/hooks/use-previous';
import { backupBTag, setReferrerUrl } from '~/store/cookies';
import useGlobalQuickDeposit from '../hooks/use-global-quick-deposit';
import { detectIncognito } from 'detectincognitojs';
import TanstackQueryProvider from '~/providers/tanstack-react-query-provider';
import useSessionValidationScheduler from '~/components/authentication/shared/utils/use-session-validation-scheduler';

const WebSocketClient = lazy(() => import('./websocket-client'));

const asyncTracking = async location => {
	dataLayerChanges(location);
	queryChanges(location);

	graphyte.trackPageView({
		path: location.pathname,
		url: window.location.href,
		search: location.search,
		title: document.title
	});
};

const App = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const prevLocation = usePrevious(location);
	// all selectors
	const isGuestPlayer = useSelector(selectIsGuestPlayer);
	const guestLocale = useSelector(selectGuestLocale);
	const authenticated = useSelector(selectAuthenticated);
	const prevAuthenticated = usePrevious(authenticated);
	const products = useSelector(selectHasProducts);
	const { hasWeSpin } = products;

	// custom hooks
	const { setUpBPEventListeners } = useDesktopStateManager();
	useSessionValidationScheduler({ authenticated, isInit: true });

	// memoized variables
	const bridgeAvailable = useMemo(() => isBridgeAvailable(), []);
	// calculate the location changes before, better to pass booleans into effects, instead of objects
	const urlChanged = useMemo(() => {
		if (!location || !prevLocation) return false;
		return (
			location.pathname !== prevLocation.pathname ||
			location.search !== prevLocation.search ||
			location.hash !== prevLocation.hash
		);
	}, [location, prevLocation]);
	const locationChanged = location.pathname !== prevLocation?.pathname;

	/**
	 * Detect if the browser is Safari in private mode and reload the page
	 * Bug on Safari when the "Use advanced tracking and fingerprinting protection" settings are enable: https://developer.apple.com/forums/thread/739549
	 */
	useEffect(() => {
		const hasReloadedSafariPrivateBrowsing = sessionStorage.getItem('hasReloadedSafariPrivateBrowsing');
		if (!hasReloadedSafariPrivateBrowsing) {
			detectIncognito().then(result => {
				if (result.isPrivate && result.browserName === 'Safari') {
					sessionStorage.setItem('hasReloadedSafariPrivateBrowsing', true);
					window.location.reload();
				}
			});
		}
	}, []);

	// Run everything you need on App mount
	useEffect(() => {
		dispatch(fetchCurrency());
		backupBTag();
		setReferrerUrl();
		// set up break point dom event listeners - refactored from <DesktopStateManager />
		setUpBPEventListeners();
		/**
		 * Set the status of verification on mount of app which will consumed by flow-blocker
		 * This is currently used on mobile apps alone
		 */
		if (bridgeAvailable) {
			dispatch(accountUpdate({ verificationRequired: !!Cookies.get('verificationRequired') }));
		}
		const validateSessionCallback = () => dispatch(validateSession());
		window.addEventListener('focus', validateSessionCallback);

		return () => {
			// Clean up any event listeners or subscriptions here
			window.removeEventListener('focus', validateSessionCallback);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// for other effects, keep it small, independent
	// run this when url changes
	useEffect(() => {
		if (urlChanged) {
			asyncTracking(location);
		}
	}, [urlChanged, location]);

	// run this only when location changes
	useEffect(() => {
		if (locationChanged) {
			dispatch(setPreviousLocation(prevLocation));
		}
	}, [isGuestPlayer, locationChanged, location, guestLocale, prevLocation, dispatch]);

	// run this when authenticated changes
	useEffect(() => {
		if (prevAuthenticated !== authenticated) {
			dispatch(updateSession(authenticated));
			graphyte.identify();
		}
	}, [authenticated, prevAuthenticated, dispatch]);
	useGlobalQuickDeposit(true); // Ensure we check QD availability early

	return (
		<>
			{/* TODO: NonAuthChange has been removed check if its okay, DesktopStateManager is moved to a hook */}
			<RootErrorBoundary ignoreErrors>
				<Suspense fallback={<NullLoader />}>
					<WebSocketClient />
				</Suspense>
			</RootErrorBoundary>

			<HelmetTagWrapper />
			<ThemeProvider>
				<TanstackQueryProvider>
					<Suspense fallback={<Spinner />}>
						<InitialUIContext>
							<CookieBannerContainer>
								<RegulationsContainer>
									{hasWeSpin && <WeSpinAppComponents />}
									{bridgeAvailable && <AppBridge />}
									<CRMComponents />
									<GuestFlowAutostart />
									<IframeSpinner />
									<ReCaptcha />
									<Template />
								</RegulationsContainer>
							</CookieBannerContainer>
						</InitialUIContext>
					</Suspense>
				</TanstackQueryProvider>
			</ThemeProvider>
		</>
	);
};

export default App;
